import twemoji from "twemoji";
import Glide from "@glidejs/glide";
import modal from "./js/modal";
import throttle from "lodash/throttle";
import Variator from "./js/variant-testing";
import tippy, { roundArrow } from "tippy.js";
import topBannerController from "./js/top-banner-controller";
import capterraTracking from "./js/capterra";

import "./css/_webfonts.scss";
import "./style.scss";
import initActionItems from "./blocks/action-item-client";

const Tests = new Variator();

analytics.ready(() => {
  Tests.trackExperiments();
});

document.addEventListener("DOMContentLoaded", () => {
  initActionItems();

  // For the reason we don't just use position: sticky here, see _header.scss
  const headerSpacer = document.querySelector(".header-spacer"),
    siteHeader = document.querySelector(".site-header");
  if (headerSpacer && siteHeader) {
    function updateHeaderSpacer() {
      if (siteHeader.classList.contains('site-header--expanded')) {
        return;
      }
      headerSpacer.style.height = `${siteHeader.offsetHeight}px`;
    }

    const throttledUpdateHeaderSpacer = throttle(updateHeaderSpacer, 100);

    window.addEventListener("resize", throttledUpdateHeaderSpacer);
    window.addEventListener("load", throttledUpdateHeaderSpacer);
    [...siteHeader.querySelectorAll("img")].forEach((el) =>
      el.addEventListener("load", throttledUpdateHeaderSpacer)
    );
    updateHeaderSpacer();
  }

  if (siteHeader) {
    function updateHeaderStyle() {
      siteHeader.classList.toggle("site-header--scrolled", window.scrollY > siteHeader.style.height);
    }
    const throttledUpdateHeaderStyle = throttle(updateHeaderStyle, 100);
    window.addEventListener("scroll", throttledUpdateHeaderStyle);

    /* *************** *
     *   MOBILE NAV    *
     * *************** */

    const hamburger = document.querySelector(".hamburger");
    if (hamburger) {
      hamburger.addEventListener("click", () => {
        siteHeader.classList.toggle("site-header--expanded");
        document.querySelector(".hamburger").classList.toggle("is-active");
        document.querySelector("body").classList.toggle("mobile-menu-expanded");
      });
    }

    const menuDropdowns = siteHeader.querySelectorAll(".menu-item-has-children > a");
    if (menuDropdowns) {
      const clickOutsideNavDropdowns = (e) => {
        const currentOpenDropdown = document.querySelector(".menu-item-has-children.open");
        if (!e.target.closest(".menu-item-has-children") && currentOpenDropdown) {
          currentOpenDropdown.classList.remove("open");
          siteHeader.classList.remove("site-header--submenu-open");
        }
      }
      window.addEventListener("click", clickOutsideNavDropdowns);

      menuDropdowns.forEach((el) => {
        el.addEventListener("click", (e) => {
          e.preventDefault();

          const menuItem = e.target.parentElement;

          Array.from(
            siteHeader.querySelectorAll(".menu-item-has-children")
          )
            .filter((el) => el != menuItem)
            .map((el) => el.classList.remove("open"));

          const isOpen = menuItem.classList.toggle("open");
          siteHeader.classList.toggle("site-header--submenu-open", isOpen);
        });

        // Prevent double-clicks from selecting the button text
        el.addEventListener("mousedown", (e) => {
          if (e.detail > 1) e.preventDefault();
        })
      });
    }
  }

  /* *************** *
   *  EMOJI SUPPORT  *
   * *************** */

  twemoji.parse(document.body, {
    base: "https://cdnjs.cloudflare.com/ajax/libs/twemoji/11.2.0/2/",
    folder: "svg",
    ext: ".svg",
  });

  /* *************** *
   *  TESTIMONIALS   *
   * *************** */

  if (document.querySelector(".glide.testimonials:not(.no-auto-switch)")) {
    let slider = new Glide(".glide.testimonials:not(.no-auto-switch)", {
      type: "carousel",
      touchAngle: 30,
      autoplay: 10000,
    }).mount();
  }

  /* ****************** *
   *  NEW TESTIMONIALS  *
   * ****************** */
  const testimonialSections = document.querySelectorAll('.testimonials-block--multiple');
  if (testimonialSections) {
    testimonialSections.forEach(testimonialSection => {
      let currentActiveIndex = 0;
      const carousel = new Glide(testimonialSection, {
        type: 'carousel',
        perView: 1,
        touchAngle: 30,
        autoplay: 10000,
        hoverpause: false,
        animationDuration: 800,
        animationTimingFunc: 'ease',
        gap: 0,
      }).mount();
      const mobileProgressBar = testimonialSection.querySelector(
        '.testimonials-block__progress-bar--mobile .testimonials-block__progress-bar--filled'
      );
      const restartProgressAnimation = progressBar => {
        if (!progressBar) {
          return;
        }
        progressBar.classList.remove('progress-animation');
        //  Trigger a DOM reflow. We should be good performance wise since this does not happens very often
        void progressBar.offsetWidth;
        progressBar.classList.add('progress-animation');
      };
      if (mobileProgressBar) {
        carousel.on('run', () => restartProgressAnimation);
        restartProgressAnimation(mobileProgressBar);
      }
      const testimonialMenuItems = testimonialSection.querySelectorAll(
        '.testimonials-block__menu-item'
      );
      if (!testimonialMenuItems) {
        return;
      }
      testimonialMenuItems.forEach((testimonialMenuItem, index) => {
        testimonialMenuItem.addEventListener('click', () => carousel.go(`=${index}`));
      });
      const changeActiveIndex = newActiveIndex => {
        testimonialMenuItems[currentActiveIndex].classList.remove('testimonials-block__menu-item--active');
        testimonialMenuItems[newActiveIndex].classList.add('testimonials-block__menu-item--active');
        const oldProgressBar = testimonialMenuItems[currentActiveIndex].querySelector('.testimonials-block__progress-bar--filled');
        if (oldProgressBar) {
          if (currentActiveIndex === newActiveIndex) {
            restartProgressAnimation(oldProgressBar);
            return;
          }
          oldProgressBar.classList.remove('progress-animation');
        }
        const newProgressBar = testimonialMenuItems[newActiveIndex].querySelector('.testimonials-block__progress-bar--filled');
        if (newProgressBar) {
          newProgressBar.classList.add('progress-animation');
        }
        currentActiveIndex = newActiveIndex;
      };
      carousel.on('run', () => changeActiveIndex(carousel.index));
      changeActiveIndex(currentActiveIndex);
    });
  }

  /* ********************** *
   *  SCREENSHOT SWITCHER   *
   * ********************** */

  if (document.querySelector(".screenshot-switcher__desktop")) {
    document
      .querySelectorAll(
        ".screenshot-switcher__desktop .screenshot-switcher__item"
      )
      .forEach((el) => {
        el.addEventListener("click", (e) => {
          e.preventDefault();

          let id = e.currentTarget.hash.substr(1);
          let slide = document.getElementById(id);

          document
            .querySelectorAll(".screenshot-switcher__item")
            .forEach((item) => {
              item.classList.toggle(
                "screenshot-switcher__item--current",
                item === e.currentTarget
              );
            });

          document
            .querySelectorAll(".screenshot-switcher__slide")
            .forEach((item) => {
              item.classList.toggle(
                "screenshot-switcher__slide--current",
                item === slide
              );
            });
        });
      });
  }
  if (document.querySelector(".screenshot-switcher__mobile")) {
    let slider = new Glide(".screenshot-switcher__mobile.glide", {
      type: "slider",
      perView: 1,
      touchAngle: 30,
      autoplay: 10000,
    }).mount();
  }

  /* ******************* *
   *  FEATURES SWITCHER  *
   * ******************* */
  const featureSections = document.querySelectorAll('.features-block');
  if (featureSections) {
    const timeInMsToNextItem = 10000;
    featureSections.forEach(featureSection => {
      const menuItemsWrapper = featureSection.querySelector('.features-block__menu');
      const imagesWrapper = featureSection.querySelector('.features-block__images');
      if (menuItemsWrapper && imagesWrapper) {
        imagesWrapper.style.height = `${menuItemsWrapper.offsetHeight}px`;
      }
      const switcherMenuItems = featureSection.querySelectorAll('.features-block__menu-item');
      const switcherImages = featureSection.querySelectorAll(
        '.features-block__images .features-block__image'
      );
      if (switcherMenuItems.length > 0 && switcherMenuItems.length === switcherImages.length) {
        let currentActiveIndex = 0;
        let currentProgressTimer;

        const nextItem = () => {
          currentActiveIndex++;
          if (currentActiveIndex >= switcherMenuItems.length) {
            currentActiveIndex = 0;
          }
          toggleItem(currentActiveIndex);
        };

        const toggleItem = index => {
          if (isNaN(index)) {
            return;
          }
          const activeMenu = featureSection.querySelector('.features-block__menu-item--active');
          const activeImage = featureSection.querySelector('.features-block__image--active');
          if (activeMenu && activeImage) {
            activeMenu.classList.remove('features-block__menu-item--active');
            activeImage.classList.remove('features-block__image--active');
            const currentProgressBar = activeMenu.querySelector(
              '.features-block__progress-bar--filled'
            );
            if (currentProgressBar) {
              currentProgressBar.classList.remove('progress-animation');
            }
          }

          switcherMenuItems[index].classList.add('features-block__menu-item--active');
          switcherImages[index].classList.add('features-block__image--active');
          const newProgressBar = switcherMenuItems[index].querySelector(
            '.features-block__progress-bar--filled'
          );
          if (currentProgressTimer) {
            clearTimeout(currentProgressTimer);
          }
          if (newProgressBar) {
            newProgressBar.classList.add('progress-animation');
            currentProgressTimer = setTimeout(nextItem, timeInMsToNextItem);
          }
          currentActiveIndex = index;
        };
        for (let index = 0; index < switcherMenuItems.length; index++) {
          switcherMenuItems[index].addEventListener('click', e => {
            e.preventDefault();
            toggleItem(index);
          });
        }
        toggleItem(currentActiveIndex);
      }

      /* ************************* *
       *  FEATURES SWITCHER MOBILE *
       * ************************* */
      const featureMobileSwitcher = featureSection.querySelector(
        '.features-block__image-switcher--mobile.glide'
      );
      if (featureMobileSwitcher) {
        const slider = new Glide(featureMobileSwitcher, {
          type: 'slider',
          perView: 1,
          touchAngle: 30,
          autoplay: 10000,
          hoverpause: false,
          animationDuration: 800,
          animationTimingFunc: 'ease',
        }).mount();
        const progressBar = featureMobileSwitcher.querySelector(
          '.features-block__progress-bar--filled'
        );
        const restartProgressAnimation = () => {
          progressBar.classList.remove('progress-animation');
          //  Trigger a DOM reflow. We should be good performance wise since this does not happens very often
          void progressBar.offsetWidth;
          progressBar.classList.add('progress-animation');
        };
        if (progressBar) {
          slider.on('run', restartProgressAnimation);
        }
        restartProgressAnimation();
      }
    });
  }

  /* *************** *
   *      LOGOS      *
   * *************** */
  const logosSections = document.querySelectorAll('.logos-block');
  if (logosSections) {
    let incrementsPerStep;
    const calculateIncrementPerStep = () => {
      if (screen.width <= 767) {
        incrementsPerStep = 2;
        return;
      }
      if (screen.width <= 1279) {
        incrementsPerStep = 3;
        return;
      }
      incrementsPerStep = 4;
    };
    calculateIncrementPerStep();
    const throttledCalculateIncrementPerStep = throttle(calculateIncrementPerStep, 100);
    window.addEventListener('resize', throttledCalculateIncrementPerStep);

    logosSections.forEach(logosSection => {
      const logosWrapper = logosSection.querySelector('ul');
      const logos = logosSection.querySelectorAll('li');
      if (!logosWrapper || !logos || logos.length <= 0) {
        return;
      }
      //  Making sure that all logos starts deactivated;
      logos.forEach(logo => {
        logo.classList.remove('active');
      });

      let currentActiveIndex = 0;
      const timeToChangeLogosInMs = 4000;
      const nextStep = () => {
        logos[currentActiveIndex].classList.remove('active');
        currentActiveIndex += incrementsPerStep;
        if (currentActiveIndex >= logos.length) {
          currentActiveIndex = 0;
        }
        logos[currentActiveIndex].classList.add('active');
        setTimeout(nextStep, timeToChangeLogosInMs);
      };
      logos[0].classList.add('active');
      setTimeout(nextStep, timeToChangeLogosInMs);
    });
  }

  /* *************** *
   *    TOOLTIPS     *
   * *************** */

  tippy("[data-fellow-tooltip]", {
    arrow: roundArrow,
    theme: "fellow",
    maxWidth: "15rem",
  });

  /* *************** *
   *   SIGNUP FORMS  *
   * *************** */

  modal();

  let modalTriggers = document.querySelectorAll("[fellow-modal-trigger]");
  if (modalTriggers.length > 0) {
    modalTriggers.forEach((el) => {
      el.addEventListener("click", (e) => {
        try {
          analytics.track("HubspotForm Opened");
        } catch (err) {}
      });
    });
  }

  let signupForms = document.querySelectorAll("[fellow-signup-form]"),
    signupButtons = document.querySelectorAll(
      [
        "[fellow-signup-button]",
        "[href^='https://app.fellow.co/auth/signup']",
        "[href^='https://app.fellow.co/auth/setup']",
        "[href^='https://fellow.app/auth/signup']",
        "[href^='https://fellow.app/auth/setup']",
      ].join(", ")
    ),
    requestADemoButtons = document.querySelectorAll(
      [
        "[fellow-request-a-demo-button]",
        "[href^='https://fellow.app/pricing/request-a-demo']",
      ].join(", ")
    );

  /* *************** *
   *      GFORMS     *
   * *************** */
  const gform = document.querySelector("form[id^='gform']");
  if (gform) {
    const splittedId = gform.id.split('_');
    if (splittedId.length === 2) {
      const formId = splittedId[1];
      // This should check for validation
      gform.addEventListener("submit", () => {
        try {
          analytics.track("HubspotForm Submitted", { formId });
        } catch (err) {}
      });
    } 
  }

  function trackAction(intent, el) {
    let noteTemplateId = null;
    let ctaId = null;
    if (el) {
      if (el.href) {
        const url = new URL(el.href);
        noteTemplateId = url.searchParams.get('tid');
      }
      if (el.dataset) {
        ctaId = el.dataset.tracking;
      }
      if (intent === "Signup Intent") {
        const linkedinPixelConversion = document.createElement("IMG");
        linkedinPixelConversion.width = "1";
        linkedinPixelConversion.height = "1";
        linkedinPixelConversion.src = "https://px.ads.linkedin.com/collect/?pid=2777122&conversionId=3589818&fmt=gif";
        document.body.appendChild(linkedinPixelConversion);
      }
    }
    try {
      analytics.track(intent, { noteTemplateId, ctaId });
      capterraTracking();
    } catch (err) {}
  }

  signupForms.forEach((el) => {
    el.addEventListener("submit", () => trackAction("Signup Intent", el));
  });

  signupButtons.forEach((el) => {
    el.addEventListener("click", () => trackAction("Signup Intent", el));

    let link = new URL(el.href);
    el.href = link.toString();
  });

  requestADemoButtons.forEach(el => {
    el.addEventListener("click", () => trackAction("Request Demo Intent", el));
  });

  /* ********************** *
   * EXTERNAL LINK TRACKING *
   * ********************** */

  function externalLinkHandler(e) {
    if (
      "ga" in window &&
      !e.defaultPrevented &&
      e.target.hostname &&
      e.target.hostname != window.location.hostname
    ) {
      ga("send", "event", {
        eventAction: "click",
        eventCategory: "outbound",
        eventLabel: e.target.href,
        transportType: "beacon",
      });
    }
  }
  document.addEventListener("click", externalLinkHandler);

  /**
   * Track Hubspot form submissions in Segment
   */
  if (window.jQuery) {
    let refCode, refSource;
    const eBookFormId = 2;
    // This is deprecated and hence why we have the analytics.track commented inside of it
    // We are using this to test linkedin's pixel but we should migrate to the GFORM
    // Tracking above
    jQuery(document).on("gform_confirmation_loaded", (event, formId) => {
      if (formId == eBookFormId) {
        const linkedinPixelConversion = document.createElement("IMG");
        linkedinPixelConversion.width = "1";
        linkedinPixelConversion.height = "1";
        linkedinPixelConversion.src = "https://px.ads.linkedin.com/collect/?pid=2777122&conversionId=3589834&fmt=gif";
        document.body.appendChild(linkedinPixelConversion);
      }
      // try {
      //   // Roughly following Segment's Noun Verbed format
      //   analytics.track("HubspotForm Submitted", { formId });
      // } catch (err) {}

      // const email = document.getElementById("fellow_gform_email"),
      //   tools = document.getElementById("fellow_gform_tools"),
      //   source = document.getElementById("fellow_gform_source");
    });
  }

  /* ********************** *
   * PILLAR PAGE NAVIGATION *
   * ********************** */

  const pillarNav = document.querySelector(".pillarpage-nav"),
    chapters = document.querySelectorAll(".fgb-pillarpage-section");

  if (pillarNav && chapters.length && headerSpacer) {
    let currentChapter = 0;

    const skipHandler = (forward) => (event) => {
      event.preventDefault();
      let target = chapters[forward ? currentChapter : currentChapter - 2];
      window.scrollTo({
        top: target.offsetTop - headerSpacer.offsetHeight,
        behavior: "smooth",
      });
    };

    const [back, forward] = pillarNav.querySelectorAll(".pillarpage-nav__skip");

    function updatePillarNav() {
      currentChapter = 0;
      let threshold = window.innerHeight * 0.4;

      while (
        chapters[currentChapter] &&
        chapters[currentChapter].getBoundingClientRect().top < threshold
      ) {
        currentChapter++;
      }

      if (chapters[currentChapter - 2]) {
        back.innerText = chapters[currentChapter - 2].querySelector(
          "small"
        ).innerText;
        back.style.opacity = 1;
      } else {
        back.style.opacity = 0;
      }

      if (chapters[currentChapter]) {
        forward.innerText = chapters[currentChapter].querySelector(
          "small"
        ).innerText;
        forward.style.opacity = 1;
      } else {
        forward.style.opacity = 0;
      }
    }

    updatePillarNav();
    back.addEventListener("click", skipHandler(false));
    forward.addEventListener("click", skipHandler(true));

    const throttledUpdate = throttle(updatePillarNav, 100);
    window.addEventListener("resize", throttledUpdate);
    window.addEventListener("scroll", throttledUpdate);

    // It's set to `display: none` by default – now that this is set up, have it show up
    pillarNav.style.display = null;
  }

  /*************************
   *      AUDIO PLAYER      *
   * ********************** */

  const audioPlayer = document.querySelector("#audioPlayer");

  if (audioPlayer) {
    const playButton = document.querySelector(".audio-player__play-button");
    const currentTimeText = document.querySelector(
      ".audio-player__current-time"
    );
    const progressBar = document.querySelector("#progressBar");
    const progressFillBar = document.querySelector(
      "#progressBar .audio-player__bar--filled"
    );
    const progressDragButton = document.querySelector(
      "#progressBar .audio-player__drag-button"
    );
    const volumeBar = document.querySelector("#volumeBar");
    const volumeFillBar = document.querySelector(
      "#volumeBar .audio-player__bar--filled"
    );
    const volumeDragButton = document.querySelector(
      "#volumeBar .audio-player__drag-button"
    );
    const durationText = document.querySelector(".audio-player__duration");
    const volumeButton = document.querySelector(
      ".audio-player__audio-volume--icon"
    );
    let draggingProgressBar = false;
    let draggingVolumeBar = false;

    const formatAudioTime = (timeInSeconds) => {
      timeInSeconds = Math.ceil(timeInSeconds);
      const hours = Math.floor(timeInSeconds / 3600);
      const minutes = Math.floor((timeInSeconds - hours * 3600) / 60);
      const seconds = timeInSeconds - hours * 3600 - minutes * 60;
      const hoursText = hours >= 10 ? hours : `0${hours}`;
      const minutesText = minutes >= 10 ? minutes : `0${minutes}`;
      const secondsText = seconds >= 10 ? seconds : `0${seconds}`;

      return `${
        hours != 0 ? `${hoursText}:` : ""
      }${minutesText}:${secondsText}`;
    };
    const toggleAudioPlay = () => {
      if (audioPlayer.paused) {
        audioPlayer.play();
        playButton.setAttribute(
          "src",
          `${fellowAssetsPrefix}img/audio-player/pause-button.svg`
        );
      } else {
        audioPlayer.pause();
        playButton.setAttribute(
          "src",
          `${fellowAssetsPrefix}img/audio-player/play-button.svg`
        );
      }
    };
    const audioEnded = () => {
      playButton.setAttribute(
        "src",
        `${fellowAssetsPrefix}img/audio-player/play-button.svg`
      );
    };
    const toggleVolumeMute = () => {
      audioPlayer.muted = !audioPlayer.muted;
      if (audioPlayer.muted) {
        volumeButton.setAttribute(
          "src",
          `${fellowAssetsPrefix}img/audio-player/volume-muted.svg`
        );
      } else {
        volumeButton.setAttribute(
          "src",
          `${fellowAssetsPrefix}img/audio-player/volume.svg`
        );
      }
    };
    const updateProgressBar = () => {
      if (currentTimeText) {
        currentTimeText.innerHTML = formatAudioTime(audioPlayer.currentTime);
      }
      const percent =
        (audioPlayer.currentTime / audioPlayer.duration) * 100 + "%";
      progressFillBar.style.width = percent;
      progressDragButton.style.left = `calc(${percent} - ${
        progressDragButton.offsetWidth / 2
      }px)`;
    };
    const changeCurrentTime = (event) => {
      const clientX = event.touches ? event.touches[0].clientX : event.clientX;
      let percent =
        (clientX - progressBar.getBoundingClientRect().left) /
        progressBar.offsetWidth;
      percent = Math.min(1, Math.max(0, percent));
      audioPlayer.currentTime = audioPlayer.duration * percent;
      if (!draggingProgressBar) {
        window.addEventListener("mousemove", changeCurrentTime);
        window.addEventListener("touchmove", changeCurrentTime);
        window.addEventListener("mouseup", stopDrag);
        window.addEventListener("touchend", stopDrag);
        draggingProgressBar = true;
      }
    };
    const updateVolumeBar = () => {
      const percent = audioPlayer.volume * 100 + "%";
      volumeFillBar.style.width = percent;
      volumeDragButton.style.left = `calc(${percent} - ${
        volumeDragButton.offsetWidth / 2
      }px)`;
    };
    const changeVolume = (event) => {
      const clientX = event.touches ? event.touches[0].clientX : event.clientX;
      let percent =
        (clientX - volumeBar.getBoundingClientRect().left) /
        volumeBar.offsetWidth;
      percent = Math.min(1, Math.max(0, percent));
      audioPlayer.volume = percent;
      if (!draggingVolumeBar) {
        window.addEventListener("mousemove", changeVolume);
        window.addEventListener("touchmove", changeVolume);
        window.addEventListener("mouseup", stopDrag);
        window.addEventListener("touchend", stopDrag);
        draggingVolumeBar = false;
      }
    };
    const stopDrag = () => {
      window.removeEventListener("mousemove", changeCurrentTime);
      window.removeEventListener("mousemove", changeVolume);
      window.removeEventListener("touchmove", changeCurrentTime);
      window.removeEventListener("touchmove", changeVolume);
      window.removeEventListener("mouseup", stopDrag);
      window.removeEventListener("touchend", stopDrag);
      draggingProgressBar = false;
      draggingVolumeBar = false;
    };
    const setupAudioPlayer = () => {
      durationText.innerHTML = formatAudioTime(audioPlayer.duration);
      updateVolumeBar();
    };

    if (playButton) {
      playButton.addEventListener("click", toggleAudioPlay);
      audioPlayer.addEventListener("ended", audioEnded);
    }
    if (volumeButton) {
      volumeButton.addEventListener("click", toggleVolumeMute);
    }
    if (progressBar && progressFillBar && progressDragButton) {
      audioPlayer.addEventListener("timeupdate", updateProgressBar);
      progressBar.addEventListener("mousedown", changeCurrentTime);
      progressBar.addEventListener("touchstart", changeCurrentTime);
      progressDragButton.addEventListener("mousedown", changeCurrentTime);
      progressDragButton.addEventListener("touchstart", changeCurrentTime);
    }
    if (volumeBar && volumeFillBar && volumeDragButton) {
      audioPlayer.addEventListener("volumechange", updateVolumeBar);
      volumeBar.addEventListener("mousedown", changeVolume);
      volumeBar.addEventListener("touchstart", changeVolume);
      volumeDragButton.addEventListener("mousedown", changeVolume);
      volumeDragButton.addEventListener("touchstart", changeVolume);
    }
    if (audioPlayer.readyState === 4) {
      setupAudioPlayer();
    } else {
      audioPlayer.addEventListener("loadedmetadata", setupAudioPlayer);
    }
  }

  /* **************************************** *
   *         FILTER AND CATEGORIZATION        *
   * **************************************** */
  const filters = document.querySelectorAll(".filter");
  if (filters && filters.length > 0) {
    filters.forEach(filter => {
      const searchInput = filter.querySelector("input[type='search']");
      const clearInputButton = filter.querySelector(".filter-categorization__clear-icon");
      if (searchInput && clearInputButton) {
        const setSearchIconVisibility = () => {
          if (searchInput.value && searchInput.value.length > 0) {
            clearInputButton.style.display = "inline";
          } else {
            clearInputButton.style.display = "none";
          }
        }
        const clickCloseIcon = () => {
          searchInput.value = "";
          searchInput.focus();
        };
        searchInput.addEventListener("focus", () => {
          setSearchIconVisibility();
          searchInput.addEventListener("input", setSearchIconVisibility);
        });
        clearInputButton.addEventListener("touchstart", clickCloseIcon);
        clearInputButton.addEventListener("mousedown", clickCloseIcon);
      }
      const filterDropdownButton = filter.querySelector(".filter-categorization__dropdown-button");
      const filterDropdownContent = filter.querySelector(".filter-categorization__dropdown-content");
      if (filterDropdownButton && filterDropdownContent) {
        //  Using listener on window and not on button to detect clicks outside of the button to close dropdown
        //  We could toggle this listener on/off only when the dropdown is open but I think that this is better
        window.addEventListener("click", (event) => {
          if (event.target === filterDropdownButton) {
            filterDropdownButton.classList.toggle("active");
            filterDropdownContent.classList.toggle("show");
            if (filterDropdownButton === document.activeElement) {
              filterDropdownButton.blur();
            }
          } else {
            filterDropdownButton.classList.remove("active");
            filterDropdownContent.classList.remove("show");
          }
        });
      }
    });
    const stickyFilterSentinel = document.querySelector(".sticky-filter__sentinel");
    const stickyFilterSentinelEnd = document.querySelector(".sticky-filter__sentinel-end");
    const stickyFilter = document.querySelector(".sticky-filter");

    if (stickyFilterSentinel && stickyFilter) {
      const updateStickyFilterPosition = () => {
        stickyFilter.style.top =  `${siteHeader.offsetHeight}px`;
      };
      const updateStickyStyle = () => {
        const bounding = stickyFilterSentinel.getBoundingClientRect();
        const endBounding = stickyFilterSentinelEnd.getBoundingClientRect();
        const startSentinelVisible = bounding.top >= 0 && bounding.left >= 0 && bounding.right <= window.innerWidth && bounding.bottom <= window.innerHeight;
        if (startSentinelVisible || endBounding.top <= siteHeader.offsetHeight + stickyFilter.offsetHeight) {
          stickyFilter.classList.remove("stuck");
        } else {
          stickyFilter.classList.add("stuck");
        }
      };
      const throttledUpdateStickyPosition = throttle(updateStickyFilterPosition, 100);
      const throttledUpdateStickyStyle = throttle(updateStickyStyle, 100);
      window.addEventListener("resize", throttledUpdateStickyPosition);
      window.addEventListener("scroll", throttledUpdateStickyStyle);
      throttledUpdateStickyPosition();
    }
  }
  const desktopFilter = document.querySelector('.templates-page aside');
  if (desktopFilter) {
    const marginTop = 32;
    const updateStickyFilterPosition = () => {
      desktopFilter.style.top =  `${siteHeader.offsetHeight + marginTop}px`;
    };
    const throttledUpdateStickyPosition = throttle(updateStickyFilterPosition, 100);
    window.addEventListener("resize", throttledUpdateStickyPosition);
    throttledUpdateStickyPosition();
  }

  /* ************************ *
   *  TEMPLATE/VIDEO SLIDERS  *
   * ************************ */
  const sliders = document.querySelectorAll('.template-page__items-cards, .videos-block > ul, .testimonial-cards-block > ul');
  if (sliders && sliders.length > 0) {
    sliders.forEach(slider => {
      let isDown = false;
      let startX;
      let scrollLeft;
      slider.addEventListener('mousedown', (e) => {
        isDown = true;
        startX = e.pageX - slider.offsetLeft;
        scrollLeft = slider.scrollLeft;
      });
      slider.addEventListener('mouseleave', () => {
        isDown = false;
      });
      slider.addEventListener('mouseup', () => {
        isDown = false;
      });
      slider.addEventListener('mousemove', (e) => {
        if(!isDown) return;
        e.preventDefault();
        const x = e.pageX - slider.offsetLeft;
        const walk = (x - startX);
        slider.scrollLeft = scrollLeft - walk;
      });    
    });
  }

  /* ************************ *
   *      FEATURE BLOCK       *
   * ************************ */
  const featureBlocks = document.querySelectorAll('.feature-block');
  if (featureBlocks && featureBlocks.length > 0) {
    featureBlocks[0].classList.add('first');
    featureBlocks[featureBlocks.length - 1].classList.add('last');
  }

  /* ************************* *
   *  TEMPLATE CATEGORY BLOCK  *
   * ************************* */
  // Get all containers holding categories
  const templateCatBlocks = document.querySelectorAll(
    ".template-category-block"
  );
  // Following code should only be run if a template categories block exists
  if (templateCatBlocks && templateCatBlocks.length > 0) {
    // Go through each block and collect reference for block's button - create event listener which adds class
    templateCatBlocks.forEach((templateCatBlock) => {
      // Get reference for button for this specific block
      const viewAllButton = templateCatBlock.querySelector(
        ".template-category-block__viewAllBtn"
      );
      const categories = templateCatBlock.querySelector(".template-category-block__categories");
      // Following code is if the button is non-existing
      if (!viewAllButton) return;

      // Add listener which adds class to templateCatBlock
      viewAllButton.addEventListener("click", () => {
        categories.classList.add("show-all");
        viewAllButton.classList.add("hidden");
      });
    });
  }

  /* ************************ *
   *        SHARE MODAL       *
   * ************************ */
  let shareModalOpen = false;
  let shareButtonForImagePreview = null;
  const shareContainers = document.querySelectorAll(".share-container");
  if (shareContainers && shareContainers.length > 0) {
    shareContainers.forEach(shareContainer => {
      const shareButton = shareContainer.querySelector(".share-button");
      if (!shareButtonForImagePreview) {
        shareButtonForImagePreview = shareButton;
      }
      const shareModal = shareContainer.querySelector(".share-modal");
      const shareModalClose = shareContainer.querySelector(".share-modal__close");
      const shareLink = shareContainer.querySelector(".share-copy-link input");
      const shareCopyLink = shareContainer.querySelector(".share-copy-link a");
      const openModal = e => {
        shareModal.style.display = "block";
        shareModalOpen = true;
        document.addEventListener("click", checkClickOutsideModal);
        if (imagePreviewOpen && closeImagePreviewModal) {
          closeImagePreviewModal();
          shareModal.scrollIntoView({ behavior: 'instant', block: 'center' });
        }
        e.stopPropagation();
      }
      const closeModal = () => {
        shareModal.style.display = "none";
        shareModalOpen = false;
        document.removeEventListener("click", checkClickOutsideModal);
      }
      const checkClickOutsideModal = e => {
        if (!e.target.closest(".share-modal") && shareModalOpen) {
          closeModal();
        }
      }
      const copyLink = () => {
        shareLink.select();
        shareLink.setSelectionRange(0, 99999); /* For mobile devices */
        document.execCommand("copy");
      }
      shareButton.addEventListener("click", openModal);
      if (shareModalClose) {
        shareModalClose.addEventListener("click", closeModal);
      }
      if (shareLink && shareCopyLink) {
        shareCopyLink.addEventListener("click", copyLink);
      }
    });
  }

  /* ************************ *
   *       IMAGE PREVIEW      *
   * ************************ */
  let imagePreviewOpen = false;
  let closeImagePreviewModal;
  const imagePreview = document.querySelector(".image-preview-container");
  const imagePreviewModal = document.querySelector(".image-preview__modal");
  const imagePreviewDimmer = document.querySelector(".image-preview__modal-dimmer");
  const imagePreviewCloseButton = document.querySelector(".image-preview__close");
  if (imagePreview && imagePreviewModal) {
    const imagePreviewShareButton = imagePreviewModal.querySelector(".share-button");
    const checkClickOutsideModal = e => {
      if (
        !e.target.closest(".image-preview__modal") &&
        !e.target.closest(".share-modal") &&
        imagePreviewOpen
      ) {
        closeImagePreviewModal();
      }
    }
    const openModal = e => {
      imagePreviewModal.style.display = "block";
      if (imagePreviewDimmer) {
        imagePreviewDimmer.style.display = "block";
      }
      document.body.style.overflowY = 'hidden';
      imagePreviewOpen = true;
      document.addEventListener("click", checkClickOutsideModal)
      e.stopPropagation();
    }
    closeImagePreviewModal = () => {
      imagePreviewModal.style.display = "none";
      if (imagePreviewDimmer) {
        imagePreviewDimmer.style.display = "none";
      }
      document.body.style.overflowY = 'scroll';
      imagePreviewOpen = false;
      document.removeEventListener("click", checkClickOutsideModal);
    }
    imagePreview.addEventListener("click", openModal);
    if (imagePreviewCloseButton) {
      imagePreviewCloseButton.addEventListener("click", closeImagePreviewModal);
    }
    imagePreviewShareButton.addEventListener("click", e => {
      if (!shareButtonForImagePreview) {
        return;
      }
      shareButtonForImagePreview.click();
      e.stopPropagation();
    });
  }

  /* ************************ *
   *       VIDEO VIEWER       *
   * ************************ */
  const videoContainers = document.querySelectorAll(".videos-block__video-container");
  const videoModal = document.querySelector(".videos-block--modal");
  if (videoContainers && videoContainers.length > 0 && videoModal) {
    let player;
    let youtubeLoaded = false;
    let videoOpened = false;
    window.onYouTubeIframeAPIReady = () => {
      youtubeLoaded = true;
      if (videoOpened) {
        player = new YT.Player("youtube-iframe");
      }
    }
    // Injecting youtube iframe API
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    const firstScriptTag = document.getElementsByTagName("script")[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

    const videoIFrame = videoModal.querySelector("iframe");
    const closeVideoModal = e => {
      if (player && player.stopVideo) {
        player.stopVideo();
      }
      document.body.style.overflowY = "scroll";
      videoModal.style.display = "none";
      videoModal.removeEventListener("click", closeVideoModal);
    };
    videoContainers.forEach(videoContainer => {
      if (!videoContainer.hasAttribute("data-url")) {
        return;
      }
      videoContainer.addEventListener("click", () => {
        videoIFrame.src = videoContainer.getAttribute("data-url") + "?enablejsapi=1";
        if (youtubeLoaded && !player) {
          player = new YT.Player("youtube-iframe");
        }
        document.body.style.overflowY = "hidden";
        videoModal.style.display = "flex";
        videoModal.addEventListener("click", closeVideoModal);
        videoOpened = true;
      });
    });
  }

  /* **************************** *
   *  TESTIMONIAL CARDS SWITCHER  *
   * **************************** */
  const testimonialCardsSections = document.querySelectorAll('.testimonial-cards-block');
  if (testimonialCardsSections && testimonialCardsSections.length > 0) {
    testimonialCardsSections.forEach(testimonialCardsSection => {
      const testimonialCardsMobileSwitcher = testimonialCardsSection.querySelector(
        '.testimonial-cards-block__cards-container--mobile.glide'
      );
      if (testimonialCardsMobileSwitcher) {
        new Glide(testimonialCardsMobileSwitcher, {
          type: 'slider',
          perView: 1,
          touchAngle: 30,
          autoplay: 10000,
          hoverpause: false,
          animationDuration: 800,
          animationTimingFunc: 'ease',
        }).mount();
      }
    });
  }

  /* **************************** *
   *    TWITTER CARDS SWITCHER    *
   * **************************** */
  const twitterCardsSections = document.querySelectorAll('.twitter-cards-block');
  if (twitterCardsSections && twitterCardsSections.length > 0) {
    twitterCardsSections.forEach(twitterCardsSection => {
      const twitterCardsMobileSwitcher = twitterCardsSection.querySelector(
        '.twitter-cards-block__cards-container--mobile.glide'
      );
      if (twitterCardsMobileSwitcher) {
        new Glide(twitterCardsMobileSwitcher, {
          type: 'slider',
          perView: 1,
          touchAngle: 30,
          autoplay: 10000,
          hoverpause: false,
          animationDuration: 800,
          animationTimingFunc: 'ease',
        }).mount();
      }
    });
  }

  /* ******************* *
   *   REFERRER MANAGER  *
   * ******************* */
  const UTM_STORE_KEY = "fellow_referrer";
  const UTM_PARAMS_OF_INTEREST = ["utm_campaign", "utm_medium", "utm_source", "utm_content", "utm_term", "fbclid"];
  const existing = window.localStorage.getItem(UTM_STORE_KEY);

  if (existing === null) {
    const result = {};
    const params = new URLSearchParams(window.location.search);
    for (const [name, value] of params.entries()) {
      if (UTM_PARAMS_OF_INTEREST.includes(name.toLowerCase())) {
        result[name.toLowerCase()] = value;
      }
    }
    window.localStorage.setItem(UTM_STORE_KEY, JSON.stringify(result));
  }

  /* ************************ *
   * TWITTER SELECTION SHARE  *
   * ************************ */
  const twitterSelectionShare = document.querySelector("a.twitter-selection-share");
  if (twitterSelectionShare) {
    function hideTwitterSelectionShare(event) {
      if (event && event.target.closest("a.twitter-selection-share")) {
        return;
      }
      twitterSelectionShare.style.display = "none";
      window.removeEventListener("click", hideTwitterSelectionShare);
    };
    function showTwitterSelectionShare (event) {
      const selection = window.getSelection();
      if (selection.toString().length > 0) {
        const textRect = selection.getRangeAt(0).getBoundingClientRect();
        const xPositionOffset = -80;
        const yPositionOffset = -25;
        const xPosition = ((textRect.right + textRect.left) / 2) + yPositionOffset + window.scrollX;
        const yPosition = (textRect.top > Math.abs(yPositionOffset) + siteHeader.offsetHeight ? textRect.top : textRect.bottom) + window.scrollY + xPositionOffset;
        twitterSelectionShare.href = `https://twitter.com/intent/tweet?text=${selection.toString()}&url=${window.location.href}`;
        twitterSelectionShare.style.left = `${xPosition}px`;
        twitterSelectionShare.style.top = `${yPosition}px`;
        twitterSelectionShare.style.display = "flex";
        event.stopPropagation();
        window.addEventListener("click", hideTwitterSelectionShare);
      }
      window.removeEventListener("click", showTwitterSelectionShare);
      return false;
    };
    window.addEventListener("selectstart", () => {
      hideTwitterSelectionShare(null);
      window.addEventListener("click", showTwitterSelectionShare);
    });
  }

  /* ************************ *
   *    SCROLL PROGRESS BAR   *
   * ************************ */
  const scrollProgressBar = document.querySelector("body > .progress-bar");
  if (scrollProgressBar) {
    const scrollProgressBarFilled = scrollProgressBar.querySelector(".progress-bar--filled");
    if (scrollProgressBarFilled) {
      function updateProgressBar() {
        const winScroll = (document.body.scrollTop || document.documentElement.scrollTop);
        const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
        const scrolled = (winScroll / height) * 100;
        scrollProgressBarFilled.style.width = `${Math.min(100, scrolled)}%`;
      }
      function updateProgressBarPosition() {
        scrollProgressBar.style.top = `${siteHeader.offsetHeight}px`;
      }

      const throttledUpdateProgressBar = throttle(updateProgressBar, 10);
      updateProgressBar();
      window.addEventListener("scroll", throttledUpdateProgressBar);

      const throttledUpdateProgressBarPosition = throttle(updateProgressBarPosition, 100);
      const heavilyThrottleUpdateProgressBarPosition = throttle(updateProgressBarPosition, 100);
      window.addEventListener("resize", throttledUpdateProgressBarPosition);
      window.addEventListener("load", throttledUpdateProgressBarPosition);
      [...siteHeader.querySelectorAll("img")].forEach((el) =>
        el.addEventListener("load", throttledUpdateProgressBarPosition)
      );
      //  This is done to handle the browser url showing when you scroll up
      //  Pushing the whole document down
      window.addEventListener("scroll", heavilyThrottleUpdateProgressBarPosition);
      updateProgressBarPosition();
    }
  }

  /* **************************************** *
   *      AFTER OPTIMIZE INITIALIZATION       *
   * **************************************** */
  window.addEventListener("OptimizeLoaded", () => {
    if (headerSpacer && siteHeader) {
      // The attribute [data-fellow-header-block] is put on any Gutenberg block
      // whose background should show up behind the header. This moves the header
      // spacer div inside that block if appropriate.
      let firstBlock = document.querySelector("body > main > :first-child");
      //  In case we are ab testing and hiding the first hero block
      if (firstBlock && getComputedStyle(firstBlock, null).display === 'none') {
        firstBlock = document.querySelector("body > main > :nth-child(2)");
      }
      if (firstBlock && firstBlock.hasAttribute("data-fellow-header-block")) {
        headerSpacer.remove();
        firstBlock.insertBefore(headerSpacer, firstBlock.childNodes[0]);
      }
    }
    /* ********************** *	
     *        TOP BANNER      *	
     * ********************** */	
    const topBanner = document.querySelector('.top-banner');	
    if (topBanner && document.querySelector('body:not(.hide-top-banner)')) {	
      const repositionPageContent = () => {
        siteHeader.style.top = `${topBanner.offsetHeight}px`;	
      }	
      const hideBanner = () => {	
        topBanner.classList.add('top-banner__hidden');	
      }	
      const removeBanner = (e) => {	
        e.preventDefault();	
        hideBanner();	
        if (siteHeader) {	
          siteHeader.style.top = 0;	
          window.removeEventListener('resize', repositionPageContent);	
        }	
        topBannerController.closedBanner();	
      }	
      if (topBannerController.isTopBannerOnCooldown()) {	
        hideBanner();	
        return;	
      }	
      const closeBanner = document.querySelector('.top-banner__close');	
      if (siteHeader) {	
        repositionPageContent();	
        window.addEventListener('resize', repositionPageContent);	
      }	
      closeBanner.addEventListener('click', removeBanner);	
    }
    /* ********************** *	
     *        LINKEDIN        *	
     * ********************** */
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
    window._linkedin_data_partner_ids.push(window._linkedin_data_partner_id);
  });
});
